import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Loader2, Filter, Mail, Video, ClipboardList, Users, Package, Download, Clock, Check, Send, HelpCircle, ExternalLink, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { SendListingFormButton } from '../components/SendListingForm';
import { StoppedCheckoutTab } from '../components/StoppedCheckoutTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Results of the automatic listing emails sent to "Request a listing" enquiries.
const AutoListingStats = () => {
  const [d, setD] = useState(null);
  useEffect(() => { axios.get(`${API}/funnel/auto-listing-stats`).then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return null;
  const pct = (a, b) => (b ? `${Math.round((a / b) * 100)}%` : '-');
  const rows = [['claim', 'Claim email'], ['create_listing', 'Create-listing form'], ['total', 'All auto emails']];
  return (
    <div className="mb-4 border border-gray-200 rounded-lg bg-white overflow-x-auto" data-testid="auto-listing-stats">
      <p className="px-4 pt-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Auto-sent listing emails</p>
      <table className="w-full text-sm">
        <thead className="text-xs text-gray-500"><tr><th className="text-left font-medium px-4 py-2">Email</th><th className="text-right font-medium px-3">Sent</th><th className="text-right font-medium px-3">Started checkout</th><th className="text-right font-medium px-3">Paid</th><th className="text-right font-medium px-4">Paid rate</th></tr></thead>
        <tbody>
          {rows.map(([k, label]) => (
            <tr key={k} className={`border-t border-gray-100 ${k === 'total' ? 'font-semibold text-gray-900' : 'text-gray-700'}`} data-testid={`auto-stats-${k}`}>
              <td className="px-4 py-2">{label}</td><td className="text-right px-3">{d[k].sent}</td><td className="text-right px-3">{d[k].started}</td>
              <td className="text-right px-3 text-emerald-700" data-testid={`auto-stats-${k}-paid`}>{d[k].paid}</td><td className="text-right px-4">{pct(d[k].paid, d[k].sent)}</td>
            </tr>))}
        </tbody>
      </table>
    </div>
  );
};

const SOURCE_LABELS = {
  how_to_choose_homecare: 'How to choose (guide)',
  homecare_webinar: 'Homecare webinar',
  homecare_questionnaire: 'Buyer questionnaire',
  for_suppliers: 'Supplier listing',
  create_listing: 'Self-serve listing',
  advisor_waitlist: 'CareTechAdvisor waitlist',
  procurement_insurance: 'Insurance value (procurement)',
  procurement_energy: 'Energy (procurement)',
  procurement_staffing: 'Agency staffing (procurement)',
  procurement_ppe: 'PPE (procurement)',
  procurement_it_telephony: 'IT & telephony (procurement)',
  procurement_training: 'Training (procurement)',
  procurement_waste: 'Waste (procurement)',
  assurance_review: 'Assurance review',
  product_interest: 'Product interest',
  interest_wizard: 'Interest wizard',
  public_site: 'Public site',
};
const SOURCE_DESCRIPTIONS = {
  contact: 'Submitted the public "Contact us" form on the website.',
  for_suppliers: 'A technology supplier asked to be listed in the Care Tech Compare directory.',
  listing: 'A technology supplier asked to be listed in the directory.',
  create_listing: 'A supplier filled in the self-serve "Create a listing" form and went to payment. Check Listing Approvals once paid.',
  interest_wizard: 'Used the on-site interest wizard and told us what they are looking for (see "Interests").',
  advisor_waitlist: 'Joined the CareTechAdvisor waitlist to be notified when the advisor tool launches.',
  buying_help_assessment: 'Completed the homecare buying-help assessment questionnaire.',
  homecare_questionnaire: 'Completed the homecare buyer-needs questionnaire.',
  homecare_webinar: 'Registered for the all-in-one homecare webinar.',
  how_to_choose_homecare: 'Requested the "How to choose homecare" buyer guide.',
  product_interest: 'Clicked "Register interest" on a specific product (see the product name).',
  procurement_it_telephony: 'Requested procurement help for IT & telephony.',
  procurement_energy: 'Requested procurement help for energy.',
  procurement_insurance: 'Requested procurement help for insurance.',
  procurement_staffing: 'Requested procurement help for agency staffing.',
  procurement_ppe: 'Requested procurement help for PPE.',
  procurement_training: 'Requested procurement help for training.',
  procurement_waste: 'Requested procurement help for waste.',
  assurance_review: 'Requested an assurance review.',
  public_site: 'Captured somewhere on the public website.',
};
const describeSource = (s) => {
  if (!s) return 'Captured by the public funnel.';
  if (SOURCE_DESCRIPTIONS[s]) return SOURCE_DESCRIPTIONS[s];
  if (s.startsWith('platform_webinar_')) {
    const slug = s.replace('platform_webinar_', '').replace(/_/g, ' ');
    return `Registered for the "${slug}" platform webinar.`;
  }
  if (s.includes('procurement') || s.endsWith('_proc')) return 'Requested procurement help (see the category).';
  return 'Captured by the public funnel.';
};
const label = (s) => {
  if (SOURCE_LABELS[s]) return SOURCE_LABELS[s];
  if (s && s.startsWith('platform_webinar_')) return 'Platform webinar';
  return s || 'unknown';
};
const fmt = (s) => { try { return new Date(s).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return s; } };
const durationBetween = (a, b) => {
  try {
    const ms = new Date(b) - new Date(a);
    if (!(ms >= 0)) return '';
    const mins = Math.round(ms / 60000);
    if (mins < 60) return `${Math.max(1, mins)}m`;
    const h = Math.round(mins / 60);
    if (h < 48) return `${h}h`;
    return `${Math.round(h / 24)}d`;
  } catch { return ''; }
};

const downloadCsv = (group) => {
  const rows = [['Name', 'Email', 'Organisation', 'Role', 'Registered']];
  (group.attendees || []).forEach((a) => rows.push([a.name || '', a.email || '', a.organisation || '', a.role || '', a.created_at || '']));
  const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `webinar-${group.slug}-attendees.csv`;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const Stat = ({ icon: Icon, value, title, testid }) => (
  <Card data-testid={testid}><CardContent className="p-5 flex items-center gap-4">
    <div className="w-11 h-11 rounded-lg bg-[#00AEEF]/10 flex items-center justify-center"><Icon className="w-5 h-5 text-[#00AEEF]" /></div>
    <div><p className="text-2xl font-bold text-gray-900">{value}</p><p className="text-sm text-gray-500">{title}</p></div>
  </CardContent></Card>
);

const STATUS_STYLE = {
  new: 'bg-amber-100 text-amber-700',
  responded: 'bg-emerald-100 text-emerald-700',
  closed: 'bg-gray-100 text-gray-600',
};

const LEAD_STATUS = {
  new: { label: 'New', cls: 'bg-amber-100 text-amber-700' },
  claimed: { label: 'Claimed', cls: 'bg-blue-100 text-blue-700' },
  followed_up: { label: 'Followed up', cls: 'bg-emerald-100 text-emerald-700' },
};

const LeadStatusControl = ({ lead, team, onUpdate }) => {
  const [busy, setBusy] = useState(false);
  const status = lead.status || 'new';
  const apply = async (s, assignedTo, assignedName) => {
    if (busy) return;
    setBusy(true);
    try {
      const body = { status: s };
      if (assignedTo !== undefined) { body.assigned_to = assignedTo; body.assigned_to_name = assignedName; }
      const { data } = await axios.post(`${API}/funnel/leads/${lead.id}/status`, body);
      onUpdate(lead.id, {
        status: data.status, status_by: s === 'new' ? null : data.by,
        assigned_to: data.assigned_to, assigned_to_name: data.assigned_to_name,
        followed_up_at: data.followed_up_at, status_at: s === 'new' ? null : new Date().toISOString(),
      });
      toast.success(s === 'new' ? 'Reset to new' : s === 'claimed' ? 'Marked as claimed' : 'Marked as followed up');
    } catch { toast.error('Could not update lead'); } finally { setBusy(false); }
  };
  const onAssign = (uid) => {
    if (!uid) return;
    const m = (team || []).find((t) => t.id === uid);
    apply(status === 'new' ? 'claimed' : status, uid, m?.name || null);
  };
  return (
    <div className="inline-flex items-center gap-2 flex-wrap justify-end" data-testid={`lead-status-${lead.id}`}>
      <div className="inline-flex items-center gap-1">
        {['new', 'claimed', 'followed_up'].map((s) => (
          <button key={s} disabled={busy} onClick={() => apply(s)}
            className={`text-[11px] px-2 py-1 rounded-md border transition-colors ${status === s ? LEAD_STATUS[s].cls + ' border-transparent font-semibold' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
            data-testid={`lead-status-${s}-${lead.id}`}>
            {LEAD_STATUS[s].label}
          </button>
        ))}
      </div>
      <select value={lead.assigned_to || ''} disabled={busy} onChange={(e) => onAssign(e.target.value)}
        className="text-[11px] border border-gray-200 rounded-md px-1.5 py-1 text-gray-600 bg-white max-w-[140px]"
        data-testid={`lead-assignee-${lead.id}`}>
        <option value="">Assign…</option>
        {(team || []).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
      </select>
    </div>
  );
};

const ProductInterestCard = ({ pi, onChange }) => {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const status = pi.status || 'new';
  const act = async (body, okMsg) => {
    setBusy(true);
    try {
      await axios.post(`${API}/funnel/product-interests/${pi.id}/update`, body);
      if (okMsg) toast.success(okMsg);
      setNote('');
      onChange();
    } catch { toast.error('Could not update enquiry'); } finally { setBusy(false); }
  };
  return (
    <Card data-testid={`funnel-product-interest-${pi.id}`}><CardContent className="p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-semibold text-gray-900">{pi.name || pi.email}</p>
          <p className="text-sm text-gray-500">{pi.email}{pi.organisation ? ` · ${pi.organisation}` : ''}</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={STATUS_STYLE[status]} data-testid={`interest-status-${pi.id}`}>{status}</Badge>
          <span className="text-xs text-gray-400">{fmt(pi.created_at)}</span>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
        <Badge className="bg-[#1D11AA]/10 text-[#1D11AA] inline-flex items-center gap-1"><Package className="w-3 h-3" /> {pi.product_name || pi.product_slug}</Badge>
        {pi.supplier_name && <span className="text-gray-500">by {pi.supplier_name}</span>}
        {pi.responded_at && <span className="inline-flex items-center gap-1 text-emerald-600"><Clock className="w-3 h-3" /> Responded in {pi.response_hours != null ? `${pi.response_hours}h` : 'n/a'}</span>}
      </div>
      {pi.message && <p className="text-sm text-gray-500 mt-2 italic">"{pi.message}"</p>}

      {(pi.notes || []).length > 0 && (
        <div className="mt-3 space-y-1.5 border-l-2 border-gray-100 pl-3">
          {(pi.notes || []).map((n, i) => (
            <p key={i} className="text-xs text-gray-500"><span className="font-medium text-gray-700">{n.by}</span> · {fmt(n.at)}<br />{n.text}</p>
          ))}
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {status !== 'responded' && <button disabled={busy} onClick={() => act({ status: 'responded' }, 'Marked as responded')} className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 rounded-md hover:bg-emerald-100" data-testid={`interest-respond-${pi.id}`}><Check className="w-3.5 h-3.5" /> Mark responded</button>}
        {status !== 'closed' && <button disabled={busy} onClick={() => act({ status: 'closed' }, 'Enquiry closed')} className="inline-flex items-center gap-1 text-xs text-gray-600 border border-gray-200 px-2.5 py-1.5 rounded-md hover:bg-gray-50" data-testid={`interest-close-${pi.id}`}>Close</button>}
        {status !== 'new' && <button disabled={busy} onClick={() => act({ status: 'new' }, 'Reopened')} className="inline-flex items-center gap-1 text-xs text-gray-600 border border-gray-200 px-2.5 py-1.5 rounded-md hover:bg-gray-50" data-testid={`interest-reopen-${pi.id}`}>Reopen</button>}
      </div>
      <div className="mt-2 flex items-center gap-2">
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a follow-up note..." className="flex-1 border border-gray-200 rounded-md px-3 py-1.5 text-sm" data-testid={`interest-note-input-${pi.id}`} />
        <button disabled={busy || !note.trim()} onClick={() => act({ note }, 'Note added')} className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-[#00A79D] hover:bg-[#00857D] px-3 py-1.5 rounded-md disabled:opacity-50" data-testid={`interest-note-save-${pi.id}`}><Send className="w-3.5 h-3.5" /> Add</button>
      </div>
    </CardContent></Card>
  );
};

const FunnelInbox = () => {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [leads, setLeads] = useState([]);
  const [stoppedCount, setStoppedCount] = useState(null);
  useEffect(() => { axios.get(`${API}/admin/sales-gate/stopped`).then((r) => setStoppedCount(r.data.items.length)).catch(() => {}); }, []);
  const [webinar, setWebinar] = useState([]);
  const [webinarGroups, setWebinarGroups] = useState([]);
  const [questionnaires, setQuestionnaires] = useState([]);
  const [productInterests, setProductInterests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sourceFilter, setSourceFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [team, setTeam] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, l, w, q, pi, tm] = await Promise.all([
        axios.get(`${API}/funnel/overview`),
        axios.get(`${API}/funnel/leads`),
        axios.get(`${API}/funnel/webinars`),
        axios.get(`${API}/funnel/questionnaires`),
        axios.get(`${API}/funnel/product-interests`),
        axios.get(`${API}/funnel/team`),
      ]);
      setOverview(o.data); setLeads(l.data.items || []);
      setWebinarGroups(w.data.webinars || []);
      setWebinar((w.data.webinars || []).flatMap((g) => g.attendees || []));
      setQuestionnaires(q.data.items || []); setProductInterests(pi.data.items || []);
      setTeam(tm.data.members || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const shownLeads = leads.filter((x) => (!sourceFilter || x.source === sourceFilter) && (!statusFilter || (x.status || 'new') === statusFilter));
  const sources = [...new Set(leads.map((x) => x.source))];
  const statusCounts = { new: 0, claimed: 0, followed_up: 0 };
  leads.forEach((x) => { const s = x.status || 'new'; statusCounts[s] = (statusCounts[s] || 0) + 1; });
  const removeSignup = async (w) => {
    if (!window.confirm(`Remove ${w.name || w.email} from this webinar's sign-ups? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/funnel/webinars/registrations/${w.id}`);
      setWebinarGroups((prev) => prev.map((g) => ({ ...g, attendees: g.attendees.filter((a) => a.id !== w.id), count: g.attendees.filter((a) => a.id !== w.id).length })));
      setWebinar((prev) => prev.filter((a) => a.id !== w.id));
      setOverview((o) => (o ? { ...o, webinar_registrations: Math.max(0, (o.webinar_registrations || 1) - 1) } : o));
      toast.success('Sign-up removed');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not remove sign-up'); }
  };
  const updateLead = (id, patch) => setLeads((prev) => prev.map((x) => (x.id === id ? { ...x, ...patch } : x)));

  if (loading) return <div className="flex items-center justify-center py-32" data-testid="funnel-loading"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="funnel-inbox-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Funnel Inbox</h1>
        <p className="text-gray-500 text-sm mt-1">Everything captured by the public Homecare funnel, guide requests, webinar sign-ups and buyer-needs questionnaires.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Users} value={overview?.leads ?? 0} title="Total leads captured" testid="funnel-stat-leads" />
        <Stat icon={Video} value={overview?.webinar_registrations ?? 0} title="Webinar registrations" testid="funnel-stat-webinar" />
        <Stat icon={ClipboardList} value={overview?.questionnaires ?? 0} title="Questionnaires" testid="funnel-stat-questionnaires" />
        <Stat icon={Mail} value={overview?.by_source?.length ?? 0} title="Active sources" testid="funnel-stat-sources" />
      </div>

      <Tabs defaultValue={new URLSearchParams(window.location.search).get('tab') || 'leads'}>
        <TabsList data-testid="funnel-tabs">
          <TabsTrigger value="leads" data-testid="funnel-tab-leads">All leads ({leads.length})</TabsTrigger>
          <TabsTrigger value="webinar" data-testid="funnel-tab-webinar">Webinar ({webinar.length})</TabsTrigger>
          <TabsTrigger value="questionnaires" data-testid="funnel-tab-questionnaires">Questionnaires ({questionnaires.length})</TabsTrigger>
          <TabsTrigger value="products" data-testid="funnel-tab-products">Product interest ({productInterests.length})</TabsTrigger>
          <TabsTrigger value="stopped" data-testid="funnel-tab-stopped">Stopped at checkout{stoppedCount != null ? ` (${stoppedCount})` : ''}</TabsTrigger>
        </TabsList>

        <TabsContent value="stopped" className="mt-4">
          <StoppedCheckoutTab onCount={setStoppedCount} />
        </TabsContent>

        <TabsContent value="leads" className="mt-4">
          <AutoListingStats />
          <div className="flex flex-wrap items-center gap-2 mb-2" data-testid="funnel-status-filter">
            <span className="text-xs font-semibold text-gray-500 inline-flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Status</span>
            {[['', 'All', leads.length], ['new', 'New', statusCounts.new], ['claimed', 'Claimed', statusCounts.claimed], ['followed_up', 'Followed up', statusCounts.followed_up]].map(([val, lab, cnt]) => (
              <button key={val || 'all'} onClick={() => setStatusFilter(val)}
                className={`text-xs px-3 py-1.5 rounded-full border ${statusFilter === val ? 'bg-[#1D11AA] text-[#ffffff] border-[#1D11AA]' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                data-testid={`funnel-status-filter-${val || 'all'}`}>{lab} ({cnt})</button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-gray-400" />
            <button onClick={() => setSourceFilter('')} className={`text-xs px-3 py-1.5 rounded-full border ${!sourceFilter ? 'bg-[#00AEEF] text-white border-[#00AEEF]' : 'border-gray-200 text-gray-600'}`} data-testid="funnel-filter-all">All</button>
            {sources.map((s) => (
              <button key={s} onClick={() => setSourceFilter(s)} className={`text-xs px-3 py-1.5 rounded-full border ${sourceFilter === s ? 'bg-[#00AEEF] text-white border-[#00AEEF]' : 'border-gray-200 text-gray-600'}`} data-testid={`funnel-filter-${s}`}>{label(s)}</button>
            ))}
          </div>
          {shownLeads.length === 0 ? <EmptyRow msg="No leads match this filter." /> : (
            <div className="space-y-2">
              {shownLeads.map((l) => {
                const st = l.status || 'new';
                return (
                <Card key={l.id} data-testid={`funnel-lead-${l.id}`}><CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    {l.contact_id ? (
                      <button onClick={() => navigate(`/admin/contacts?open=${l.contact_id}`)}
                        className="font-semibold text-gray-900 hover:text-[#1D11AA] inline-flex items-center gap-1 text-left"
                        title="Open contact record to view full relationship & action history"
                        data-testid={`lead-open-${l.id}`}>
                        {l.name || l.email} <ExternalLink className="w-3 h-3 text-gray-400" />
                      </button>
                    ) : (
                      <p className="font-semibold text-gray-900" title="No CRM contact record linked to this email yet">{l.name || l.email}</p>
                    )}
                    <p className="text-sm text-gray-500">{l.email}{l.organisation ? ` · ${l.organisation}` : ''}</p>
                    {l.interest && <p className="text-xs text-gray-400 mt-0.5">{l.interest}</p>}
                    {st !== 'new' && (
                      <p className="text-[11px] text-gray-400 mt-0.5" data-testid={`lead-status-note-${l.id}`}>
                        {LEAD_STATUS[st]?.label}
                        {l.assigned_to_name ? ` · ${l.assigned_to_name}` : (l.status_by ? ` · ${l.status_by}` : '')}
                        {st === 'followed_up' && l.followed_up_at ? ` · followed up ${fmt(l.followed_up_at)}${durationBetween(l.created_at, l.followed_up_at) ? ` (${durationBetween(l.created_at, l.followed_up_at)} after capture)` : ''}` : ''}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-3 flex-wrap justify-end">
                    {l.auto_listing_email && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200" data-testid={`lead-auto-email-${l.id}`} title={l.matched_company ? `Matched existing listing: ${l.matched_company}` : 'No existing listing matched'}>Auto-sent {l.auto_listing_email === 'claim' ? 'claim email' : 'create-listing form'}</span>}
                    {(l.source === 'for_suppliers' || l.interest === 'listing') && <SendListingFormButton lead={l} onSent={(id, patch) => setLeads((prev) => prev.map((x) => (x.id === id ? { ...x, ...patch } : x)))} />}
                    <LeadStatusControl lead={l} team={team} onUpdate={updateLead} />
                    <span className="inline-flex items-center gap-1">
                      <Badge variant="secondary" className="bg-[#00AEEF]/10 text-[#0077b6]">{label(l.source)}</Badge>
                      <HelpCircle className="w-3.5 h-3.5 text-gray-400 cursor-help" title={describeSource(l.source)} data-testid={`lead-source-help-${l.id}`} />
                    </span>
                    <span className="text-xs text-gray-400">{fmt(l.created_at)}</span>
                  </div>
                </CardContent></Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="webinar" className="mt-4">
          {webinarGroups.length === 0 || webinar.length === 0 ? <EmptyRow msg="No webinar registrations yet." /> : (
            <div className="space-y-6" data-testid="funnel-webinar-groups">
              {webinarGroups.filter((g) => g.count > 0).map((g) => (
                <div key={g.slug} data-testid={`funnel-webinar-group-${g.slug}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                    <div>
                      <h3 className="font-semibold text-gray-900">{g.title}</h3>
                      <p className="text-xs text-gray-500">{g.when_label || 'Various dates'} · <span className="font-semibold text-[#0077b6]">{g.count} signed up</span></p>
                    </div>
                    <button
                      onClick={() => downloadCsv(g)}
                      className="text-xs font-semibold px-3 py-1.5 rounded-md border border-[#00AEEF] text-[#0077b6] hover:bg-[#00AEEF]/5 inline-flex items-center gap-1.5"
                      data-testid={`funnel-webinar-csv-${g.slug}`}>
                      <Download className="w-3.5 h-3.5" /> Export CSV
                    </button>
                  </div>
                  <div className="space-y-2">
                    {g.attendees.map((w) => (
                      <Card key={w.id} data-testid={`funnel-webinar-${w.id}`}><CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <p className="font-semibold text-gray-900">{w.name || w.email}</p>
                          <p className="text-sm text-gray-500">{w.email}{w.organisation ? ` · ${w.organisation}` : ''}{w.role ? ` · ${w.role}` : ''}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-gray-400">{fmt(w.created_at)}</span>
                          <button onClick={() => removeSignup(w)} title="Remove this sign-up (test or duplicate)"
                            className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                            data-testid={`funnel-webinar-delete-${w.id}`}><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </CardContent></Card>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="questionnaires" className="mt-4">
          {questionnaires.length === 0 ? <EmptyRow msg="No questionnaires submitted yet." /> : (
            <div className="space-y-3">
              {questionnaires.map((q) => (
                <Card key={q.id} data-testid={`funnel-questionnaire-${q.id}`}><CardContent className="p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                    <div>
                      <p className="font-semibold text-gray-900">{q.name || q.email}</p>
                      <p className="text-sm text-gray-500">{q.email}{q.organisation ? ` · ${q.organisation}` : ''}</p>
                    </div>
                    <span className="text-xs text-gray-400">{fmt(q.created_at)}</span>
                  </div>
                  <div className="flex flex-wrap gap-3 text-sm text-gray-600">
                    {q.provider_type && <span><span className="text-gray-400">Type:</span> {q.provider_type}</span>}
                    {q.num_locations && <span><span className="text-gray-400">Locations:</span> {q.num_locations}</span>}
                    {q.timeline && <span><span className="text-gray-400">Timeline:</span> {q.timeline}</span>}
                    {q.current_systems && <span><span className="text-gray-400">Today:</span> {q.current_systems}</span>}
                  </div>
                  {q.priorities?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {q.priorities.map((p) => <Badge key={p} variant="outline" className="text-xs">{p}</Badge>)}
                    </div>
                  )}
                  {q.notes && <p className="text-sm text-gray-500 mt-2 italic">“{q.notes}”</p>}
                </CardContent></Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="products" className="mt-4">
          {productInterests.length === 0 ? <EmptyRow msg="No product interest captured yet." /> : (
            <div className="space-y-2">
              {productInterests.map((pi) => (
                <ProductInterestCard key={pi.id} pi={pi} onChange={load} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

const EmptyRow = ({ msg }) => (
  <Card><CardContent className="p-10 text-center text-gray-400 text-sm">{msg}</CardContent></Card>
);

export default FunnelInbox;
